/**
 * Metro 2 Student Loan Timeline Panel
 * 
 * Specialized review for student loan accounts:
 * - Federal vs Private identification
 * - Deferment/Forbearance review
 * - In-school status review
 * - IDR/$0 payment review
 * - Federal On-Ramp reporting review
 * - Transfer/Consolidation review
 * - Payment timeline conflicts
 */

import React, { useState } from 'react';
import {
  GraduationCap,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Calendar,
  DollarSign,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

// Student loan issue configurations
const STUDENT_LOAN_ISSUES = {
  student_loan_timeline_review_needed: {
    label: 'Student Loan Timeline Review Needed',
    icon: Calendar,
    severity: 'medium',
    description: 'Student loan payment timeline requires verification before determining accuracy.',
  },
  protected_period_reporting_conflict: {
    label: 'Protected Period Reporting Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Late payments reported during protected period (deferment, forbearance, or in-school status).',
  },
  federal_onramp_reporting_review: {
    label: 'Federal On-Ramp Reporting Review',
    icon: Info,
    severity: 'medium',
    description: '2025 sudden late payment may be related to federal on-ramp period ending.',
  },
  no_payment_due_reporting_conflict: {
    label: 'No Payment Due Reporting Conflict',
    icon: DollarSign,
    severity: 'high',
    description: 'Late payments reported when no payment was due.',
  },
  idr_zero_payment_conflict: {
    label: 'IDR / $0 Payment Conflict',
    icon: DollarSign,
    severity: 'high',
    description: 'Late payments reported while on Income-Driven Repayment with $0 payment.',
  },
  forbearance_deferment_conflict: {
    label: 'Forbearance / Deferment Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Late payments reported during forbearance or deferment period.',
  },
  student_loan_transfer_servicing_conflict: {
    label: 'Student Loan Transfer / Servicing Conflict',
    icon: AlertCircle,
    severity: 'medium',
    description: 'Reporting issues related to servicer transfer or loan consolidation.',
  },
  student_loan_chronology_conflict: {
    label: 'Student Loan Chronology Conflict',
    icon: Calendar,
    severity: 'high',
    description: 'Payment history shows illogical timeline or date conflicts.',
  },
  bureau_student_loan_reporting_inconsistency: {
    label: 'Bureau Student Loan Reporting Inconsistency',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Same student loan reports differently across bureaus.',
  },
};

// Severity badge colors
const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
};

// Student Loan Issue Item
const StudentLoanIssueItem = ({ issueKey, details }) => {
  const config = STUDENT_LOAN_ISSUES[issueKey];
  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${
      config.severity === 'high' 
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700' 
        : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
    }`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`w-5 h-5 mt-0.5 ${
          config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
        }`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${
              config.severity === 'high' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
            }`}>
              {config.label}
            </span>
            <Badge variant="outline" className={SEVERITY_COLORS[config.severity]}>
              {config.severity}
            </Badge>
          </div>
          <p className={`text-sm ${
            config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
          }`}>
            {details || config.description}
          </p>
        </div>
      </div>
    </div>
  );
};

// Student Loan Account Details
const StudentLoanAccountDetails = ({ account }) => {
  const servicer = account?.servicer || account?.creditor_name || account?.creditorName;
  const loanType = account?.loan_type || (account?.is_federal ? 'Federal' : 'Private');
  const paymentStatus = account?.payment_status || account?.paymentStatus;
  const monthlyPayment = account?.monthly_payment || account?.payment_amount;
  const balance = account?.balance || 0;
  const defermentStatus = account?.deferment_status || account?.forbearance_status;
  const lastPayment = account?.date_last_payment || account?.dateLastPayment;
  const reportedLateMonths = account?.reported_late_months || [];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Servicer</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">{servicer || 'N/A'}</span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Loan Type</span>
        <Badge variant="outline" className={
          loanType === 'Federal' 
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
        }>
          {loanType || 'Unknown'}
        </Badge>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Payment Status</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">{paymentStatus || 'N/A'}</span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Balance</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
          ${(balance || 0).toLocaleString()}
        </span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Monthly Payment</span>
        <span className={`text-sm font-medium ${
          monthlyPayment === 0 || monthlyPayment === '$0' 
            ? 'text-blue-600 dark:text-blue-400' 
            : 'text-stone-800 dark:text-stone-200'
        }`}>
          {monthlyPayment ? `$${typeof monthlyPayment === 'number' ? monthlyPayment.toLocaleString() : monthlyPayment}` : 'N/A'}
        </span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Deferment/Forbearance</span>
        <span className={`text-sm font-medium ${
          defermentStatus ? 'text-blue-600 dark:text-blue-400' : 'text-stone-800 dark:text-stone-200'
        }`}>
          {defermentStatus || 'None'}
        </span>
      </div>
      {reportedLateMonths.length > 0 && (
        <div className="col-span-full">
          <span className="text-xs text-stone-500 dark:text-stone-400 block mb-1">Reported Late Months</span>
          <div className="flex flex-wrap gap-1">
            {reportedLateMonths.map((month, idx) => (
              <Badge key={idx} variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-xs">
                {month}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// User Context Questions
const StudentLoanContextQuestions = ({ context, onContextChange }) => {
  const [expanded, setExpanded] = useState(false);

  const questions = [
    { key: 'was_in_deferment', label: 'Were you in deferment or forbearance during the reported late period?' },
    { key: 'was_in_school', label: 'Were you in school (at least half-time) during the reported late period?' },
    { key: 'had_zero_payment', label: 'Did you have a $0 payment due (IDR or other plan)?' },
    { key: 'loan_transferred', label: 'Was the loan transferred or consolidated during this time?' },
    { key: 'had_admin_forbearance', label: 'Was there administrative forbearance (COVID, natural disaster, etc.)?' },
    { key: 'has_proof', label: 'Do you have proof from servicer or StudentAid.gov?' },
  ];

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        className="w-full justify-between text-stone-700 dark:text-stone-300"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4" />
          Answer Context Questions
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {expanded && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-3">
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
            These questions help determine if the late payments were reported during a protected period.
          </p>
          {questions.map((q) => (
            <div key={q.key} className="flex items-start space-x-3">
              <Checkbox
                id={q.key}
                checked={context?.[q.key] || false}
                onCheckedChange={(checked) => onContextChange?.({ ...context, [q.key]: checked })}
              />
              <Label htmlFor={q.key} className="text-sm text-stone-700 dark:text-stone-300 cursor-pointer">
                {q.label}
              </Label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Metro 2 Student Loan Timeline Panel
const Metro2StudentLoanPanel = ({
  account,
  bureauData,
  detectedIssues = [],
  paymentHistory,
  context = {},
  onContextChange,
  showAccountDetails = true,
  showContextQuestions = true,
}) => {
  const hasIssues = detectedIssues.length > 0;
  const highSeverityCount = detectedIssues.filter(i => STUDENT_LOAN_ISSUES[i]?.severity === 'high').length;

  // Determine dispute strength based on context
  const hasProtectedPeriod = context.was_in_deferment || context.was_in_school || 
    context.had_zero_payment || context.had_admin_forbearance;
  const hasEvidence = context.has_proof;

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="metro2-student-loan-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <GraduationCap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Student Loan Timeline Review
          </CardTitle>
          <Badge 
            variant="outline" 
            className={hasIssues 
              ? highSeverityCount > 0
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            }
          >
            {hasIssues ? `${detectedIssues.length} Issue(s)` : 'Clean'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Student Loan Notice */}
        <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
          <GraduationCap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertTitle className="text-blue-700 dark:text-blue-300">Student Loan Account Detected</AlertTitle>
          <AlertDescription className="text-blue-600 dark:text-blue-400">
            Student loans require special timeline review. 2025 sudden 90+ day late = Review Needed, 
            not automatic violation. If deferment/forbearance/in-school/$0 due applies = Strong Dispute Candidate. 
            If payment was due after on-ramp and no protected status exists = Possibly accurate / Review Needed.
          </AlertDescription>
        </Alert>

        {/* Account Details */}
        {showAccountDetails && account && (
          <StudentLoanAccountDetails account={account} />
        )}

        {/* Context Questions */}
        {showContextQuestions && (
          <StudentLoanContextQuestions context={context} onContextChange={onContextChange} />
        )}

        {/* Dispute Strength Indicator */}
        {hasProtectedPeriod && (
          <div className={`p-3 rounded-lg border ${
            hasEvidence 
              ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700'
              : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
          }`}>
            <div className="flex items-center gap-2">
              {hasEvidence ? (
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              )}
              <div>
                <span className={`font-medium ${
                  hasEvidence ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'
                }`}>
                  {hasEvidence ? 'Strong Dispute Candidate' : 'Moderate Dispute Candidate'}
                </span>
                <p className={`text-sm ${
                  hasEvidence ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {hasEvidence 
                    ? 'Protected period indicated with supporting evidence.'
                    : 'Protected period indicated. Evidence from servicer or StudentAid.gov will strengthen dispute.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Detected Issues */}
        {hasIssues ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
              Detected Student Loan Issues
            </h4>
            {detectedIssues.map((issueKey, idx) => (
              <StudentLoanIssueItem key={idx} issueKey={issueKey} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              No student loan timeline conflicts detected. Standard verification may still apply.
            </p>
          </div>
        )}

        {/* Evidence Guidance */}
        <div className="p-3 bg-stone-100 dark:bg-stone-800 rounded-lg">
          <p className="text-xs text-stone-600 dark:text-stone-400">
            <strong>Evidence Sources:</strong> StudentAid.gov account history, servicer payment history, 
            deferment/forbearance approval letters, in-school enrollment verification, IDR plan approval.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Metro2StudentLoanPanel;
export { StudentLoanIssueItem, StudentLoanAccountDetails, StudentLoanContextQuestions, STUDENT_LOAN_ISSUES };
